<template>
    <section class="section-brands">
      <div class="container container-brands text-center">
        <h2 class="brands-title fw-bold mb-2">Brands We Work With</h2>
        <p class="brands-subtitle mb-4 text-secondary">Trusted by leading franchise brands across India</p>
        <div class="marquee-container">
          <!-- Top Row: Right to Left -->
          <div ref="marqueeLeft" class="custom-marquee marquee-left">
            <div class="marquee-track" :style="leftTrackStyle">
              <div
                v-for="(logo, idx) in logos"
                :key="'top-' + idx"
                class="logo-item"
              >
                <img :src="logo" alt="Brand Logo" class="brand-logo" />
              </div>
              <!-- Duplicate for seamless loop -->
              <div
                v-for="(logo, idx) in logos"
                :key="'top-dup-' + idx"
                class="logo-item"
              >
                <img :src="logo" alt="Brand Logo" class="brand-logo" />
              </div>
            </div>
          </div>
          <!-- Bottom Row: Left to Right -->
          <div ref="marqueeRight" class="custom-marquee marquee-right">
            <div class="marquee-track" :style="rightTrackStyle">
              <div
                v-for="(logo, idx) in reversedLogos"
                :key="'bottom-' + idx"
                class="logo-item"
              >
                <img :src="logo" alt="Brand Logo" class="brand-logo" />
              </div>
              <!-- Duplicate for seamless loop -->
              <div
                v-for="(logo, idx) in reversedLogos"
                :key="'bottom-dup-' + idx"
                class="logo-item"
              >
                <img :src="logo" alt="Brand Logo" class="brand-logo" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  </template>
  
  <script setup>
  import { computed, onMounted, ref, onUnmounted } from 'vue'
  import { useFranchiseStore } from '@/stores/franchiseStore'
  
  const franchiseStore = useFranchiseStore()
  const marqueeLeft = ref(null)
  const marqueeRight = ref(null)
  const leftOffset = ref(0)
  const rightOffset = ref(0)
  const speed = 0.8 // Reduced speed for smoother movement
  let leftAnimation, rightAnimation
  
  onMounted(() => {
    if (!franchiseStore.franchises.length) {
      franchiseStore.fetchFranchises()
    }
    startMarquee()
  })
  
  onUnmounted(() => {
    if (leftAnimation) cancelAnimationFrame(leftAnimation)
    if (rightAnimation) cancelAnimationFrame(rightAnimation)
  })
  
  const logos = computed(() =>
    Array.from(
      new Set(
        franchiseStore.franchises
          .map(f => f.business_logo)
          .filter(Boolean)
      )
    )
  )
  const reversedLogos = computed(() => [...logos.value].reverse())
  
  // Improved animation logic
  function startMarquee() {
    // Left marquee (right-to-left)
    function animateLeft() {
      if (!marqueeLeft.value) return
      const track = marqueeLeft.value.querySelector('.marquee-track')
      if (!track) return
      
      const trackWidth = track.scrollWidth / 2
      leftOffset.value -= speed
      
      // Reset position when first set of logos is out of view
      if (Math.abs(leftOffset.value) >= trackWidth) {
        leftOffset.value = 0
      }
      
      leftAnimation = requestAnimationFrame(animateLeft)
    }
    
    // Right marquee (left-to-right)
    function animateRight() {
      if (!marqueeRight.value) return
      const track = marqueeRight.value.querySelector('.marquee-track')
      if (!track) return
      
      const trackWidth = track.scrollWidth / 2
      rightOffset.value += speed
      
      // Reset position when first set of logos is out of view
      if (rightOffset.value >= trackWidth) {
        rightOffset.value = 0
      }
      
      rightAnimation = requestAnimationFrame(animateRight)
    }
    
    animateLeft()
    animateRight()
  }
  
  // Computed styles for smooth movement
  const leftTrackStyle = computed(() => ({
    transform: `translateX(${leftOffset.value}px)`
  }))
  const rightTrackStyle = computed(() => ({
    transform: `translateX(${rightOffset.value}px)`
  }))
  </script>
  
  <style scoped>
  .section-brands {
    background: transparent;
    padding: 80px 0 64px 0;
  }
  .container-brands {
    max-width: 1400px;
    margin: 0 auto;
    background: transparent;
    border-radius: 0;
    box-shadow: none;
    padding: 0;
  }
  .brands-title {
    font-size: 2.8rem;
    font-weight: 900;
    color: #000000;
    margin-bottom: 16px;
    letter-spacing: -0.5px;
    text-shadow: 0 2px 4px rgba(0,0,0,0.1);
  }
  .brands-subtitle {
    font-size: 1.3rem;
    color: #6c757d;
    margin-bottom: 48px;
    line-height: 1.6;
    max-width: 600px;
    margin-left: auto;
    margin-right: auto;
    padding: 0 20px;
  }
  .marquee-container {
    position: relative;
    overflow: hidden;
    margin-top: 20px;
  }
  .custom-marquee {
    overflow: hidden;
    width: 100%;
    position: relative;
    height: 120px;
    margin-bottom: 40px;
    background: transparent;
    border-radius: 0;
    box-shadow: none;
    padding: 20px 0;
    border: none;
  }
  .custom-marquee:last-child {
    margin-bottom: 0;
  }
  .marquee-track {
    display: flex;
    width: max-content;
    will-change: transform;
    align-items: center;
    gap: 0;
  }
  .logo-item {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0 1px;
    min-width: unset;
  }
  .brand-logo {
    height: 70px;
    width: 180px;
    object-fit: contain;
    display: block;
    filter: grayscale(0.3) brightness(0.95);
    transition: all 0.3s ease;
    border-radius: 8px;
    box-shadow: none;
    background: transparent;
    padding: 0;
    border: none;
  }
  .brand-logo:hover {
    filter: grayscale(0) brightness(1.05);
    transform: scale(1.08) translateY(-3px);
    box-shadow: 0 8px 24px rgba(255,107,53,0.15);
    background: transparent;
    border-color: transparent;
  }
  /* Responsive Design */
  @media (max-width: 1200px) {
    .container-brands {
      max-width: 100%;
      margin: 0 20px;
      padding: 40px 24px 32px 24px;
    }
    
    .brands-title {
      font-size: 2.2rem;
      margin-bottom: 14px;
    }
    
    .brands-subtitle {
      font-size: 1.1rem;
      margin-bottom: 36px;
      padding: 0 16px;
    }
    
    .marquee-container {
      margin-top: 16px;
    }
    
    .custom-marquee {
      height: 100px;
      margin-bottom: 32px;
      padding: 16px 0;
    }
    
    .logo-item {
      padding: 0 12px;
      min-width: 90px;
    }
    
    .brand-logo {
      height: 60px;
      width: 150px;
    }
  }
  @media (max-width: 768px) {
    .section-brands {
      padding: 48px 0 32px 0;
    }
    
    .container-brands {
      padding: 32px 16px 24px 16px;
      border-radius: 16px;
    }
    
    .brands-title {
      font-size: 1.8rem;
      margin-bottom: 12px;
    }
    
    .brands-subtitle {
      font-size: 1rem;
      margin-bottom: 24px;
      padding: 0 12px;
    }
    
    .marquee-container {
      margin-top: 12px;
    }
    
    .custom-marquee {
      height: 80px;
      margin-bottom: 24px;
      padding: 12px 0;
      border-radius: 12px;
    }
    
    .logo-item {
      padding: 0 8px;
      min-width: 70px;
    }
    
    .brand-logo {
      height: 50px;
      width: 120px;
      border-radius: 8px;
    }
  }
  @media (max-width: 480px) {
    .section-brands {
      padding: 32px 0 24px 0;
    }
    
    .container-brands {
      padding: 24px 12px 20px 12px;
    }
    
    .brands-title {
      font-size: 1.5rem;
      margin-bottom: 10px;
    }
    
    .brands-subtitle {
      font-size: 0.9rem;
      margin-bottom: 20px;
      padding: 0 8px;
    }
    
    .marquee-container {
      margin-top: 8px;
    }
    
    .custom-marquee {
      height: 70px;
      margin-bottom: 20px;
      padding: 8px 0;
    }
    
    .logo-item {
      padding: 0 4px;
      min-width: 50px;
    }
    
    .brand-logo {
      height: 40px;
      width: 100px;
      border-radius: 6px;
    }
  }
  </style>

<!--
====================
BrandsWeWorkWith.vue - Custom Marquee Docs
====================

How it works:
- Uses two rows of logos, each row is a marquee (one right-to-left, one left-to-right)
- Uses custom JavaScript (requestAnimationFrame) for smooth, continuous scrolling
- Duplicates the logo list in each row for seamless looping
- Uses Bootstrap for spacing and alignment
- Logos are pulled from the Pinia store (real API data)

How to use:
1. Place this file in src/components/BrandsWeWorkWith.vue
2. Add <BrandsWeWorkWith /> to your page/component
3. Make sure your Pinia store is set up and provides franchise data with business_logo fields
4. Adjust the .brand-logo CSS for logo size/uniformity
5. Adjust the speed variable in <script setup> for faster/slower scrolling

How to customize:
- Change the speed variable for scroll speed
- Change the height/width in .brand-logo for logo size
- Add more/less spacing with mx-4 or adjust flex settings
- You can use any array of image URLs for the logos array

How to troubleshoot:
- If logos do not scroll, check that the Pinia store is loaded and provides logo URLs
- If you see flicker or jump, ensure the logo list is duplicated in the DOM for seamless looping
- If you want to pause on hover, add event listeners to cancelAnimationFrame and restart on mouseleave

This solution is framework-agnostic and does not require any external plugin.
-->